import { ApiError } from "./api";
import type { ApprovalCondition, Operator, Rule, RuleAction } from "./rules";

/** The three kinds of a rule, in the order the editor offers them ("Prüfen" = default, as before). */
export const RULE_ACTION_OPTIONS: {
  value: RuleAction;
  label: string;
  hint: string;
}[] = [
  {
    value: "review",
    label: "Prüfen",
    hint: "Landet in der Freigabe und wartet auf eine Entscheidung von Hand.",
  },
  {
    value: "allow",
    label: "Immer zulassen",
    hint: "Wird ohne Prüfung angenommen und ganz normal verarbeitet, inklusive Meldung an Meta und Google.",
  },
  {
    value: "reject",
    label: "Immer ablehnen",
    hint: "Geht nirgendwohin: nicht an Meta oder Google und nicht in die Auswertung.",
  },
];

export function ruleActionLabel(action: RuleAction): string {
  return RULE_ACTION_OPTIONS.find((o) => o.value === action)?.label ?? "Prüfen";
}

export function ruleDisplayName(rule: Pick<Rule, "id" | "name">): string {
  return rule.name?.trim() ? rule.name : `Regel #${rule.id}`;
}

const OPERATOR_TEXT: Record<string, string> = {
  ">": ">",
  ">=": "≥",
  "<": "<",
  "<=": "≤",
  "==": "=",
  "!=": "≠",
};

export function describeCondition(condition: ApprovalCondition | null): string {
  if (!condition) return "alle Events";
  if (condition.operator === "exists") return `${condition.field} ist gesetzt`;
  return `${condition.field} ${OPERATOR_TEXT[condition.operator] ?? condition.operator} ${condition.value}`;
}

/** The rule the server names in a 409 `rule_conflict`. */
interface ConflictingRule {
  id: number;
  name: string | null;
  action: RuleAction;
  event_type: string;
  condition: ApprovalCondition | null;
  is_global: boolean;
  project_ids: number[];
}

/**
 * Plain-German explanation of a refused save, or null when the error is
 * something else. Names the colliding rule, its kind and what it covers.
 */
export function ruleConflictMessage(err: unknown): string | null {
  if (!(err instanceof ApiError) || err.status !== 409) return null;
  const body = err.body as
    | { error?: string; conflicting_rule?: ConflictingRule }
    | undefined;
  const other = body?.conflicting_rule;
  if (body?.error !== "rule_conflict" || !other) return null;
  const scope = other.is_global ? "für alle Projekte" : "für bestimmte Projekte";
  return (
    `Diese Regel widerspricht „${ruleDisplayName(other)}“ ` +
    `(${ruleActionLabel(other.action).toLowerCase()}, ${other.event_type === "*" ? "alle Event-Typen" : other.event_type}, ` +
    `${describeCondition(other.condition)}, ${scope}). ` +
    `Zulassen- und Ablehn-Regeln dürfen sich nicht überschneiden. ` +
    `Passe den Wertebereich an, wähle einen anderen Event-Typ oder schalte die andere Regel zuerst aus.`
  );
}

/** Operators the editor offers for a field: test_contact is a yes/no field, only "=" makes sense. */
export function operatorsForField<T extends { v: string }>(field: string, all: T[]): T[] {
  return isTestContactField(field) ? all.filter((o) => o.v === "==") : all;
}

/** test_contact is a yes/no field: the editor shows a Ja/Nein choice instead of free text. */
export function isTestContactField(field: string): boolean {
  return field.trim() === "test_contact";
}

/**
 * What to set when the rule's field changes: moving TO test_contact always resets
 * to "= true" (a leftover value such as 1000 would be refused by the server),
 * everything else stays as typed.
 */
export function conditionOnFieldChange(
  prevField: string,
  nextField: string,
): { operator: Operator; value: string } | null {
  return isTestContactField(nextField) && !isTestContactField(prevField)
    ? { operator: "==", value: "true" }
    : null;
}

/** Preselected project of "+ Neue Approval-Rule" in the project tab: `?projectId=<id>`. */
export function projectIdFromSearch(search: string): number | null {
  const n = Number(new URLSearchParams(search).get("projectId"));
  return Number.isInteger(n) && n > 0 ? n : null;
}
